import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ClientLayout, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  FileText, 
  Shield, 
  AlertTriangle,
  Server,
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  X
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  'Business Continuity': FileText,
  'Disaster Recovery': Server,
  'Security': Shield,
  'SOC 2 Compliance': ShieldCheck,
};

export default function TemplateSelection() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);
  const [soc2Modal, setSoc2Modal] = useState(null);
  const [selectedCriteria, setSelectedCriteria] = useState([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = async (template) => {
    // If SOC 2, show criteria selection modal first
    if (template.soc2_selectable_criteria) {
      const required = (template.soc2_criteria_categories || [])
        .filter(c => c.required)
        .map(c => c.id);
      setSelectedCriteria(required);
      setSoc2Modal(template);
      return;
    }
    await createSubmission(template.id, null);
  };

  const createSubmission = async (templateId, criteria) => {
    setCreating(templateId);
    try {
      const body = { template_id: templateId };
      if (criteria) body.selected_criteria = criteria;
      const response = await axios.post(`${API_URL}/submissions`, body);
      toast.success('Submission created');
      navigate(`/submissions/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to create submission');
      setCreating(null);
    }
  };

  const toggleCriteria = (id, required) => {
    if (required) return;
    setSelectedCriteria(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const handleSoc2Confirm = () => {
    if (!soc2Modal) return;
    createSubmission(soc2Modal.id, selectedCriteria);
    setSoc2Modal(null);
  };

  return (
    <ClientLayout>
      <div className="page-container py-8">
        <PageHeader
          title="Choose a Template"
          description="Select a compliance template to begin filling out your information."
          breadcrumbs={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Templates' }
          ]}
        />

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-slate-500">Loading templates...</p>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {templates.map((template) => {
              const Icon = categoryIcons[template.category] || FileText;
              return (
                <div
                  key={template.id}
                  className="template-card group"
                  data-testid={`template-card-${template.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-lg flex items-center justify-center group-hover:bg-[#FF6B35] transition-colors duration-200">
                      <Icon className="h-6 w-6 text-[#FF6B35] group-hover:text-white transition-colors duration-200" />
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-xs font-medium text-[#FF6B35] bg-[#FF6B35]/10 px-3 py-1 rounded-full">
                        {template.category}
                      </span>
                      {template.iso_standard && (
                        <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full" data-testid={`iso-badge-${template.id}`}>
                          {template.iso_standard}
                        </span>
                      )}
                    </div>
                  </div>

                  <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {template.name}
                  </h3>
                  <p className="text-slate-600 mb-6">
                    {template.description}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div className="text-sm text-slate-500">
                      {template.sections?.length || 0} sections
                    </div>
                    <Button
                      onClick={() => handleSelectTemplate(template)}
                      disabled={creating === template.id}
                      className="btn-primary px-4 py-2"
                      data-testid={`select-template-${template.id}`}
                    >
                      {creating === template.id ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Creating...
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          Start
                          <ArrowRight className="h-4 w-4" />
                        </div>
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Info Section */}
        <div className="mt-12 bg-slate-50 rounded-xl p-6 border border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            About Our Templates
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-slate-900">ISO Compliant</h4>
                <p className="text-sm text-slate-600">Templates aligned with ISO 22301, ISO 27001, ISO 27035, and ISO 27031 standards.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-slate-900">Auto-Save</h4>
                <p className="text-sm text-slate-600">Your progress is automatically saved so you can continue anytime.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-slate-900">PDF Export</h4>
                <p className="text-sm text-slate-600">Export your completed documents as professional PDF files.</p>
              </div>
            </div>
          </div>
        </div>
        {/* SOC 2 Criteria Selection Modal */}
        {soc2Modal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl" data-testid="soc2-criteria-modal">
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Select Trust Service Criteria
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">Choose which SOC 2 criteria categories to include in your assessment.</p>
                  </div>
                  <button onClick={() => setSoc2Modal(null)} className="text-slate-400 hover:text-slate-600">
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-3 max-h-[400px] overflow-y-auto">
                {(soc2Modal.soc2_criteria_categories || []).map(cat => {
                  const isSelected = selectedCriteria.includes(cat.id);
                  return (
                    <button
                      key={cat.id}
                      onClick={() => toggleCriteria(cat.id, cat.required)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-[#FF6B35] bg-[#FF6B35]/5'
                          : 'border-slate-200 hover:border-slate-300'
                      } ${cat.required ? 'cursor-default' : 'cursor-pointer'}`}
                      data-testid={`criteria-${cat.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-slate-900">{cat.label}</span>
                            {cat.required && (
                              <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium">Required</span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1">{cat.description}</p>
                        </div>
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ml-3 ${
                          isSelected ? 'bg-[#FF6B35] border-[#FF6B35]' : 'border-slate-300'
                        }`}>
                          {isSelected && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="p-6 border-t border-slate-100 flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  {selectedCriteria.length} of {soc2Modal.soc2_criteria_categories?.length || 0} criteria selected
                </p>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => {
                    setSelectedCriteria((soc2Modal.soc2_criteria_categories || []).map(c => c.id));
                  }} data-testid="select-all-criteria-btn">
                    Select All
                  </Button>
                  <Button
                    onClick={handleSoc2Confirm}
                    disabled={selectedCriteria.length === 0 || creating}
                    className="btn-primary px-6"
                    data-testid="confirm-soc2-btn"
                  >
                    {creating ? 'Creating...' : 'Start Assessment'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ClientLayout>
  );
}
